// /app/frontend/src/components/lojista/FreightCoupons.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiTag, FiPlus, FiCopy, FiCheck, FiX, FiRefreshCw,
  FiAlertTriangle, FiLink, FiTrash2, FiShare2
} from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';

const FreightCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form states
  const [newCode, setNewCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(25);
  const [maxUses, setMaxUses] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const loadCoupons = useCallback(async () => {
    try {
      const response = await api.get('/coupons/freight');
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Erro ao carregar cupons:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleCreate = async () => {
    setCreateError('');
    setCreateSuccess('');

    if (!newCode.trim()) {
      setCreateError('Digite um código para o cupom');
      return;
    }

    if (newCode.trim().length < 4) {
      setCreateError('O código deve ter pelo menos 4 caracteres');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/coupons/freight', {
        code: newCode.trim().toUpperCase(),
        discount_percent: discountPercent,
        max_uses: maxUses ? parseInt(maxUses) : null,
        expires_in_days: expiresInDays
      });

      setCreateSuccess(`Cupom ${response.data.code} criado com sucesso!`);
      setTimeout(() => {
        setShowCreateModal(false);
        setNewCode('');
        setMaxUses('');
        setDiscountPercent(25);
        setExpiresInDays(30);
        setCreateSuccess('');
        loadCoupons();
      }, 1500);
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Erro ao criar cupom');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (couponId, currentStatus) => {
    try {
      await api.put(`/coupons/freight/${couponId}`, {
        is_active: !currentStatus
      });
      loadCoupons();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Desativar este cupom?')) return;
    try {
      await api.delete(`/coupons/freight/${couponId}`);
      loadCoupons();
    } catch (error) {
      console.error('Erro ao excluir cupom:', error);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(type);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const shareLink = async (link, code) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Cupom de Frete Grátis - ${code}`,
          text: `Use o cupom ${code} para desconto no frete!`,
          url: link
        });
      } catch (error) {
        console.log('Compartilhamento cancelado');
      }
    } else {
      copyToClipboard(link, `link-${code}`);
    }
  };

  const getDiscountBadgeColor = (percent) => {
    if (percent === 100) return 'bg-green-500 text-white';
    if (percent >= 70) return 'bg-blue-500 text-white';
    if (percent >= 50) return 'bg-yellow-500 text-white';
    return 'bg-gray-500 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="freight-coupons">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <FiTag className="text-red-500" />
            Cupons de Desconto no Frete
          </h3>
          <p className="text-sm text-gray-500">
            Crie cupons para seus clientes economizarem no frete
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          data-testid="create-coupon-btn"
        >
          <FiPlus />
          Novo Cupom
        </button>
      </div>

      {/* Aviso importante */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FiAlertTriangle className="text-yellow-600 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Importante!</h4>
            <p className="text-sm text-yellow-700">
              Ao criar um cupom de desconto no frete, <strong>você (lojista) será responsável por pagar 
              o valor do desconto à plataforma</strong>. Por exemplo, se o frete custa R$ 15,00 e você 
              criar um cupom de 100% de desconto, você pagará os R$ 15,00 para a plataforma.
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Cupons */}
      {coupons.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FiTag className="mx-auto text-4xl text-gray-400 mb-3" />
          <p className="text-gray-500">Nenhum cupom criado ainda</p>
          <p className="text-sm text-gray-400">Crie seu primeiro cupom para atrair mais clientes!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {coupons.map((coupon) => {
            const isExpired = new Date(coupon.expires_at) < new Date();
            const isExhausted = coupon.max_uses && coupon.uses_count >= coupon.max_uses;
            const isDisabled = !coupon.is_active || isExpired || isExhausted;

            return (
              <div
                key={coupon.id}
                className={`bg-white rounded-xl p-4 border shadow-sm ${isDisabled ? 'opacity-60' : ''}`}
                data-testid={`coupon-${coupon.code}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {/* Código do cupom */}
                      <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                        <span className="font-mono font-bold text-lg text-gray-800">
                          {coupon.code}
                        </span>
                        <button
                          onClick={() => copyToClipboard(coupon.code, `code-${coupon.code}`)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Copiar código"
                        >
                          {copiedCode === `code-${coupon.code}` ? (
                            <FiCheck className="text-green-500" />
                          ) : (
                            <FiCopy size={16} />
                          )}
                        </button>
                      </div>
                      
                      {/* Badge de desconto */}
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getDiscountBadgeColor(coupon.discount_percent)}`}>
                        {coupon.discount_percent}% OFF
                      </span>

                      {/* Status */}
                      {isExpired && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">Expirado</span>
                      )}
                      {isExhausted && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs">Esgotado</span>
                      )}
                      {!coupon.is_active && !isExpired && !isExhausted && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Desativado</span>
                      )}
                    </div>

                    {/* Informações */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Usos:</span>
                        <span className="ml-1 font-medium">
                          {coupon.uses_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ' (ilimitado)'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Validade:</span>
                        <span className="ml-1 font-medium">
                          {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Criado:</span>
                        <span className="ml-1 font-medium">
                          {new Date(coupon.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Tipo:</span>
                        <span className="ml-1 font-medium">
                          {coupon.is_global ? 'Global' : 'Sua loja'}
                        </span>
                      </div>
                    </div>

                    {/* Link */}
                    <div className="mt-3 flex items-center gap-2">
                      <FiLink className="text-gray-400" size={14} />
                      <span className="text-xs text-gray-500 truncate flex-1">
                        {coupon.link}
                      </span>
                      <button
                        onClick={() => copyToClipboard(coupon.link, `link-${coupon.code}`)}
                        className="text-blue-500 hover:text-blue-700 text-xs"
                      >
                        {copiedCode === `link-${coupon.code}` ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => shareLink(coupon.link, coupon.code)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                      title="Compartilhar"
                    >
                      <FiShare2 size={14} />
                      Enviar
                    </button>
                    {!coupon.is_global && (
                      <>
                        <button
                          onClick={() => handleToggleStatus(coupon.id, coupon.is_active)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                            coupon.is_active
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {coupon.is_active ? 'Pausar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Criar Cupom */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" data-testid="create-coupon-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Criar Cupom de Frete</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Aviso */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-start gap-2">
                <FiAlertTriangle className="flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Atenção:</strong> O valor do desconto será cobrado de você. 
                  Ex: Frete de R$ 15 com 100% de desconto = você paga R$ 15 para a plataforma.
                </span>
              </p>
            </div>

            {createError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">{createError}</p>
              </div>
            )}

            {createSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-700 flex items-center gap-1">
                  <FiCheck /> {createSuccess}
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código do Cupom *
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="Ex: FRETEGRATIS"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono"
                  maxLength={20}
                  data-testid="coupon-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">Apenas letras e números, sem espaços</p>
              </div>

              {/* Desconto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Porcentagem de Desconto *
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[25, 50, 70, 100].map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      onClick={() => setDiscountPercent(percent)}
                      className={`py-2 rounded-lg font-bold transition-all ${
                        discountPercent === percent
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`discount-${percent}`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {discountPercent === 100 
                    ? 'Frete grátis total para o cliente!'
                    : `Cliente paga ${100 - discountPercent}% do frete (${formatCurrency(15 * (100 - discountPercent) / 100)})`
                  }
                </p>
              </div>

              {/* Limite de usos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Limite de Usos (opcional)
                </label>
                <input
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="Deixe vazio para ilimitado"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="1"
                />
              </div>

              {/* Validade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Validade (dias)
                </label>
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value={7}>7 dias</option>
                  <option value={15}>15 dias</option>
                  <option value={30}>30 dias</option>
                  <option value={60}>60 dias</option>
                  <option value={90}>90 dias</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                disabled={creating}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newCode.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-create-btn"
              >
                {creating ? (
                  <>
                    <FiRefreshCw className="animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Criar Cupom
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreightCoupons;
